import { Fout } from './api'

/* ===========================================================================
 *  Praten met Cowork: opnemen, omzetten, voorlezen
 *
 *  Casper: "kan je zorgen dat als je ergens op drukt, je hele gesprekken kan
 *  voeren hiermee? dus dat je net als met chatgpt vragen stelt, en hij praat
 *  tegen je met antwoorden en of vragen."
 *
 *  Drie stukken, en bij elk is gekozen voor wat op de eigen spullen blijft:
 *
 *    Opnemen       hier in de browser (MediaRecorder), met een eenvoudige
 *                  stiltedetector: zodra je even niets zegt, is de vraag af.
 *    Omzetten      NIET de spraakherkenning van de browser -- Chrome stuurt
 *                  die naar Google, Edge naar Microsoft. De opname gaat naar
 *                  de kantoor-pc en Whisper zet hem daar om (/api/spraak,
 *                  migratie 0201, lezer/spraak.mjs).
 *    Voorlezen     speechSynthesis, en dan alleen een stem die op het apparaat
 *                  zelf staat (localService). Edge heeft ook mooie "Online"-
 *                  stemmen, maar die sturen het antwoord naar Microsoft.
 * =========================================================================== */

/** Kan deze browser het? Microfoon, opnemen en voorlezen. */
export function kanPraten(): boolean {
  return typeof window !== 'undefined'
    && !!navigator.mediaDevices?.getUserMedia
    && typeof MediaRecorder !== 'undefined'
    && 'speechSynthesis' in window
}

/* ------------------------------------------------------------------ *
 *  Voorlezen
 * ------------------------------------------------------------------ */

/** De stemmen komen in sommige browsers pas na een tel binnen. */
export function stemmen(): Promise<SpeechSynthesisVoice[]> {
  return new Promise((klaar) => {
    const nu = speechSynthesis.getVoices()
    if (nu.length) return klaar(nu)
    const t = setTimeout(() => klaar(speechSynthesis.getVoices()), 1500)
    speechSynthesis.addEventListener('voiceschanged', () => { clearTimeout(t); klaar(speechSynthesis.getVoices()) }, { once: true })
  })
}

/** Een Nederlandse stem die op dit apparaat staat; null als die er niet is. */
export async function kiesStem(): Promise<SpeechSynthesisVoice | null> {
  const nl = (await stemmen()).filter((v) => /^nl/i.test(v.lang) && v.localService)
  return nl.find((v) => /nl[-_]NL/i.test(v.lang)) ?? nl[0] ?? null
}

/**
 * Van markdown naar wat je hardop zegt. Het model hoort in een gesproken
 * gesprek geen tabellen te maken (prompt.ts), maar doet het toch soms; die
 * lees je niet voor, die staan op het scherm.
 */
export function spreekbaar(md: string): string {
  return md
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/^\s*\|.*\|\s*$/gm, ' ')
    .replace(/!\[[^\]]*\]\([^)]*\)/g, ' ')
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .replace(/https?:\/\/\S+/g, ' ')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/^\s*[-*•]\s+/gm, '')
    .replace(/^\s*\d+[.)]\s+/gm, '')
    .replace(/[*_`~>]+/g, '')
    .replace(/€\s?(-?[\d.]+(,\d+)?)/g, '$1 euro')
    .replace(/€/g, 'euro')
    .replace(/\s+/g, ' ')
    .trim()
}

/** In zinnen: Chrome stopt een lange uitspraak na een seconde of vijftien. */
export function zinnen(t: string): string[] {
  const uit: string[] = []
  for (const z of t.match(/[^.!?]+[.!?]+["')]*|[^.!?]+$/g) ?? []) {
    const s = z.trim()
    if (!s) continue
    if (uit.length && uit[uit.length - 1]!.length < 40) uit[uit.length - 1] += ' ' + s
    else uit.push(s)
  }
  return uit
}

/**
 * Voorlezen, zin voor zin. Stopt meteen bij `signaal`.
 * @param opZin  krijgt de zin die nu klinkt (voor de ondertitel)
 */
export async function spreek(tekst: string, stem: SpeechSynthesisVoice, signaal: AbortSignal, opZin?: (z: string) => void): Promise<void> {
  speechSynthesis.cancel()
  for (const z of zinnen(tekst)) {
    if (signaal.aborted) return
    opZin?.(z)
    await new Promise<void>((klaar) => {
      const u = new SpeechSynthesisUtterance(z)
      u.voice = stem
      u.lang = stem.lang
      u.rate = 1.05
      const weg = () => { speechSynthesis.cancel(); klaar() }
      signaal.addEventListener('abort', weg, { once: true })
      u.onend = () => { signaal.removeEventListener('abort', weg); klaar() }
      u.onerror = () => { signaal.removeEventListener('abort', weg); klaar() }
      speechSynthesis.speak(u)
    })
  }
  opZin?.('')
}

/** iOS laat pas voorlezen na een tik; dit is die eerste keer, binnen de tik. */
export function wekStem() {
  try { speechSynthesis.speak(new SpeechSynthesisUtterance('')) } catch { /* dan niet */ }
}

/* ------------------------------------------------------------------ *
 *  Opnemen, met een stiltedetector
 * ------------------------------------------------------------------ */

const STIL_NA_MS = 1200
const MAX_MS = 60_000
const NIETS_NA_MS = 20_000

function soortOpname(): string {
  for (const s of ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus']) {
    if (MediaRecorder.isTypeSupported(s)) return s
  }
  return ''
}

export class Microfoon {
  private stroom: MediaStream | null = null
  private ctx: AudioContext | null = null
  private meter: AnalyserNode | null = null

  /** Toestemming vragen en de microfoon openen. Gooit met een leesbare melding. */
  async open() {
    if (this.stroom) return
    try {
      this.stroom = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, channelCount: 1 },
      })
    } catch (e) {
      const naam = e instanceof DOMException ? e.name : ''
      throw new Error(naam === 'NotAllowedError'
        ? 'Cowork mag de microfoon niet gebruiken. Sta het toe via het slotje links van het adres, en probeer opnieuw.'
        : naam === 'NotFoundError' ? 'Er is geen microfoon gevonden.' : 'De microfoon ging niet open: ' + String(e))
    }
    this.ctx = new AudioContext()
    const bron = this.ctx.createMediaStreamSource(this.stroom)
    this.meter = this.ctx.createAnalyser()
    this.meter.fftSize = 1024
    bron.connect(this.meter)
  }

  /** Hoe hard het nu is, 0..1 (voor de bol op het scherm). */
  niveau(): number {
    if (!this.meter) return 0
    const buf = new Float32Array(this.meter.fftSize)
    this.meter.getFloatTimeDomainData(buf)
    let som = 0
    for (const x of buf) som += x * x
    return Math.sqrt(som / buf.length)
  }

  /**
   * Luisteren tot er iets gezegd is en het daarna even stil is.
   * Geeft de opname, of null als er niets werd gezegd (of als het werd afgebroken).
   */
  async luister(signaal: AbortSignal, opNiveau?: (n: number) => void): Promise<Blob | null> {
    if (!this.stroom) await this.open()
    if (this.ctx?.state === 'suspended') await this.ctx.resume()
    const soort = soortOpname()
    const rec = new MediaRecorder(this.stroom!, soort ? { mimeType: soort } : undefined)
    const delen: Blob[] = []
    rec.ondataavailable = (e) => { if (e.data.size) delen.push(e.data) }

    return await new Promise<Blob | null>((klaar) => {
      const begin = Date.now()
      let vloer = 0
      let ijk = 0
      let gesproken = 0
      let laatsteGeluid = 0
      let af = false

      const stop = (houden: boolean) => {
        if (af) return
        af = true
        clearInterval(klok)
        signaal.removeEventListener('abort', weg)
        rec.onstop = () => klaar(houden && delen.length ? new Blob(delen, { type: rec.mimeType || soort || 'audio/webm' }) : null)
        try { rec.stop() } catch { klaar(null) }
      }
      const weg = () => stop(false)
      signaal.addEventListener('abort', weg, { once: true })

      const klok = setInterval(() => {
        const n = this.niveau()
        opNiveau?.(n)
        const t = Date.now() - begin
        /* De eerste vierhonderd milliseconden: hoe hard is de stilte hier? */
        if (t < 400) { vloer += n; ijk++; return }
        const drempel = Math.max(0.012, (vloer / Math.max(1, ijk)) * 2.8)
        if (n > drempel) { gesproken += 50; laatsteGeluid = Date.now() }
        if (gesproken >= 250 && Date.now() - laatsteGeluid > STIL_NA_MS) return stop(true)
        if (t > MAX_MS) return stop(true)
        if (gesproken < 250 && t > NIETS_NA_MS) return stop(false)
      }, 50)

      rec.start(250)
    })
  }

  sluit() {
    this.stroom?.getTracks().forEach((t) => t.stop())
    void this.ctx?.close().catch(() => {})
    this.stroom = null
    this.ctx = null
    this.meter = null
  }
}

/* ------------------------------------------------------------------ *
 *  Omzetten, op de kantoor-pc
 * ------------------------------------------------------------------ */

export async function naarTekst(opname: Blob, signaal?: AbortSignal): Promise<string> {
  let a: Response
  try {
    a = await fetch('/api/spraak', {
      method: 'POST',
      headers: { 'Content-Type': opname.type || 'audio/webm' },
      body: opname,
      signal: signaal,
    })
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') throw e
    throw new Fout(0, 'Geen verbinding. Controleer je internet en probeer het opnieuw.')
  }
  let lijf: { ok?: boolean; fout?: string; tekst?: string } = {}
  try { lijf = await a.json() } catch { /* dan de status */ }
  if (!a.ok || lijf.ok === false) throw new Fout(a.status, lijf.fout ?? `De server gaf ${a.status}.`)
  return String(lijf.tekst ?? '')
}
